/*
    WeatherData Component
    This component is used to display the weather data for a given game.
    For MLB, it will display the weather data including: 
        - Projected temperature, high low and wind chill
        - Wind speed
        - Wind direction
        - Humidity
        - Forecasted Weather

    For NFL, it will display the weather data including:
        - Projected temperature, high low and wind chill
        - Wind speed
        - Forecasted Weather

    This component should be a clean UI, using tailwind to create a nice looking grid layout. 
    
    Props: 
        - game: the game object
        - sport: the sport of the game
        - stadium: the stadium object

*/

import React from 'react';
import { 
    CloudIcon   
} from '@heroicons/react/24/outline';
import GaugeSVG from '../../../assets/images/icons/gauge.svg';
import MlbWindDirection from './MlbWindDirection';

const WeatherData = ({game, sport, stadiumOrientation}) => {
    // Only render for MLB games with weather data
    if (!sport || !["mlb", "nfl"].includes(sport) || !game) {
        return null;
    }

    return (
        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg shadow-md p-6 mx-auto">
            <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-gray-800 dark:text-white">Weather Conditions</h2>
                <div className="w-8 h-8 bg-blue-100 dark:bg-blue-900 rounded-full flex items-center justify-center">
                    <CloudIcon className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                </div>
            </div>

            {/* Row 1: Temperature Data with Icons */}
            <div className="grid grid-cols-3 gap-4 mb-6">
                {/* High Temperature */}
                <div className="text-center">
                    <div className="text-sm text-gray-600 dark:text-gray-400 mb-1">High</div>
                    <div className="text-lg font-semibold text-gray-800 dark:text-white">
                        {game.ForecastTempHigh ? `${game.ForecastTempHigh}°` : 'N/A'}
                    </div>
                </div>

                {/* Low Temperature */}
                <div className="text-center">
                    <div className="text-sm text-gray-600 dark:text-gray-400 mb-1">Low</div>
                    <div className="text-lg font-semibold text-gray-800 dark:text-white">
                        {game.ForecastTempLow ? `${game.ForecastTempLow}°` : 'N/A'}
                    </div>
                </div>

                {/* Feels Like */}
                <div className="text-center">
                    <div className="text-sm text-gray-600 dark:text-gray-400 mb-1">Feels Like</div>
                    <div className="text-lg font-semibold text-gray-800 dark:text-white">
                        {game.ForecastWindChill ? `${game.ForecastWindChill}°` : 'N/A'}
                    </div>
                </div>
            </div>

            {/* Row 2: Forecasted Weather */}
            <div className="mb-6">
                <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                    <div className="flex flex-col items-center text-center space-y-2">
                        <div className="text-sm text-gray-600 dark:text-gray-400">Forecast</div>
                        <div className="text-lg font-medium text-gray-800 dark:text-white">
                            {game.ForecastDescription || 'No forecast available'}
                        </div>
                    </div>
                </div>
            </div>

            {/* Row 3: Wind Speed and Direction */}
            <div className={sport === "mlb" ? "grid grid-cols-2 gap-3" : "flex justify-center"}>
                {/* Wind Speed */}
                <div className="text-center">
                    <div className="flex justify-center mb-1">
                        <img src={GaugeSVG} alt="Gauge" className="
                            w-10 h-10
                            [filter:brightness(1.2)_contrast(0.8)_grayscale(1)]
                            dark:[filter:brightness(0)_invert(1)]
                        " />
                    </div>
                    <div className="text-sm text-gray-600 dark:text-gray-400 mb-1">Wind Speed</div>
                    <div className="text-lg font-semibold text-gray-800 dark:text-white">
                        {game.ForecastWindSpeed ? `${game.ForecastWindSpeed} mph` : 'N/A'}
                    </div>
                </div>
                {sport === "mlb" && (
                    //  Wind Direction 
                    <div className="flex justify-center">
                        <MlbWindDirection stadiumDegrees={stadiumOrientation?.orientation} windDegrees={game.ForecastWindDirection} />
                    </div>
                )}
            </div>
        </div>
    )
}

export default WeatherData